import React, { Component, Fragment, useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { connect } from "react-redux";
import clamp from "lodash/clamp";
import eachDayOfInterval from "date-fns/eachDayOfInterval";
import getISODay from "date-fns/getISODay";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import size from "lodash/size";
import sumBy from "lodash/sumBy";
import times from "lodash/times";

import * as dateUtils from "../../utils/date";

import "./TodoWarning.scss";

export function TodoWarning({ initialDate, entries, startDate, endDate, showWarning, totalHours, workingHours, submitted: apiSubmitted, worked: apiWorked }) {
  const { expected, worked, submitted, total, unaccounted, unsubmitted } = useCalculateActuallyWorked(entries, workingHours, totalHours, initialDate, startDate, endDate);

  if (!showWarning) {
    return null;
  }

  if (unaccounted <= 0 && unsubmitted <= 0) {
    return null;
  }

  return (
    <div className="todo-warning">
      <span className="highlight">{"\u26a0"}</span>&nbsp;
      In this period,&nbsp;
      {(unaccounted > 0)
        ? (<span><strong>{unaccounted}</strong> hours from the previous day(s) are unaccounted for.</span>)
        : null
      }
      {unaccounted && unsubmitted ? <Fragment>&nbsp;</Fragment> : null}
      {(unsubmitted > 0)
        ? (<span><strong>{unsubmitted}</strong> hours from the previous day(s) are unsubmitted.</span>)
        : null
      }
    </div>
  );
}

TodoWarning.displayName = "History.TodoWarning";

TodoWarning.propTypes = {
  activeFilters:  PropTypes.number.isRequired,
  historyContext: PropTypes.oneOf(["all", "submitted", "todo"]).isRequired,
  endDate:      PropTypes.string,
  entrySize:    PropTypes.number.isRequired,
  initialDate:  PropTypes.string,
  showWarning:  PropTypes.bool.isRequired,
  startDate:    PropTypes.string,
  submitted:    PropTypes.number.isRequired,
  worked:       PropTypes.number.isRequired,
  workingHours: PropTypes.arrayOf(PropTypes.object).isRequired
};

TodoWarning.defaultProps = {
  activeFilters:  0,
  endDate:        dateUtils.dateToApiString(moment()),
  entrySize:      0,
  historyContext: "todo",
  showWarning:    false,
  startDate:      dateUtils.dateToApiString(dateUtils.HISTORY_MAX),
  submitted:      0,
  worked:         0,
  workingHours:   [],
};

function mapStateToProps(state, ownProps) {
  const activeFilters = state.historyFilters.activeFilters;

  const historyContext = state.historyFilters.context;

  const initialDate = state.historyFilters.initialDate;

  const totalHours = state.historyFilters.totalHours;

  const entrySize = size(get(state, "entries.entries", []));

  const showWarning = historyContext === "todo" && initialDate && activeFilters === 0 && entrySize > 0;

  return {
    activeFilters, historyContext, entrySize, showWarning,
    endDate: state.historyFilters.endDate,
    entries: state.entries.entries,
    initialDate,
    submitted: state.entries.submittedHours,
    totalHours,
    worked: state.entries.hours,
    startDate: state.historyFilters.startDate,
    workingHours: state.settings.workingHours,
  };
}

export default connect(mapStateToProps)(TodoWarning);

export function useWorkingHours(workingHours) {
  return useMemo(function calculateHours() {
    return times(7).map(function(index) {
      const { workingHours: totalHours, enabled } = workingHours[index];

      return Boolean(enabled) ? parseInt(totalHours, 10) : 0
    });
  }, [workingHours]);
}

function getCurrentWorkday() {
  return moment().set({
    hour: 18,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
}

function getYesterday() {
  return getCurrentWorkday().subtract(1, "day")
}

export function useCalculateActuallyWorked(entries, workingHours, totalHours, initialDate, startDate, endDate) {
  const start = useMemo(function() {
    return moment(startDate || initialDate);
  }, [initialDate, startDate]);

  const end = useMemo(function() {
    const currentWorkday = getCurrentWorkday();

    const yesterday = getYesterday();

    const end = end ? moment(endDate) : moment();

    if (!end.isValid()) {
      if (process.env.NODE_ENV === "development") {
        console.warn("end date is not valid, defaulting to yesterday")
        console.dir({ end, endDate });
      }

      return yesterday;
    } else if (end.isSame(currentWorkday, "day") && end.isBefore(currentWorkday)) {
      if (process.env.NODE_ENV === "development") {
        // console.debug("current workday is not over, defaulting to yesterday");
      }

      return yesterday;
    } else if (end.isAfter(currentWorkday)) {
      if (process.env.NODE_ENV === "development") {
        // console.debug("end is after current workday, capping at current start");
      }

      return currentWorkday.clone().startOf("day");
    } else {
      return end;
    }
  }, [endDate]);

  const hours = useWorkingHours(workingHours);

  const byDate = useMemo(function() {
    return groupBy(entries, function(entry) {
      return entry.entryDate.format("YYYY-MM-DD");
    });
  }, [entries])

  const expected = useCalculateTotalWorkingHours(start, end, hours);

  return useMemo(function() {
    const totals = { expected, total: 0, worked: 0, overtime: 0, submitted: 0 };

    for (const [key, serverHours] of Object.entries(totalHours)) {
      const entryDate = moment(key);

      if (entryDate.isBefore(start) || entryDate.isAfter(end)) {
        continue;
      }

      const expected = getExpectedHoursFor(entryDate.toDate(), hours);

      const entries = byDate[key];

      const firstEntry = Array.isArray(entries) ? entries[0] : null;

      if (!firstEntry) {
        const total = serverHours.total;
        const submitted = clamp(total, 0, serverHours.submitted);
        const worked = clamp(total, 0, expected);
        const overtime = total > expected ? total - expected : 0;

        const missing = expected > total ? expected - total : 0;

        if (process.env.NODE_ENV === "development" && missing) {
          // console.debug("missing time on %s: %d hour(s) out of %d", key, missing, expected);
        }

        totals.total += total;
        totals.worked += worked;
        totals.overtime += overtime;
        totals.submitted += submitted;

        continue;
      }

      const total = firstEntry.noEntries ? get(serverHours, "total", 0) : (sumBy(entries, "hours") || get(serverHours, "total", 0));

      const submitted = firstEntry.noEntries ? get(serverHours, "submitted", 0) : (sumBy(entries, function({ submitted, hours }) {
        return submitted && hours ? hours : 0;
      }) || clamp(total, 0, serverHours.submitted));

      const worked = clamp(total, 0, expected);

      const overtime = total > expected ? total - expected : 0;

      const missing = expected > total ? expected - total : 0;

      if (overtime) {
        // console.debug("found overtime on %s: %d hour(s) over %d (%d total)", key, overtime, expected, total);
      }

      if (process.env.NODE_ENV === "development" && missing) {
        // console.debug("missing time on %s: %d hour(s) out of %d", key, missing, expected);
      }

      totals.total += total;
      totals.worked += worked;
      totals.overtime += overtime;
      totals.submitted += submitted;
    }

    totals.unaccounted = totals.expected - totals.worked;
    totals.unsubmitted = totals.worked - totals.submitted;

    // Assume overtime can make up for missing hours on other days
    if (totals.unaccounted > 0 && totals.overtime >= totals.unaccounted) {
      totals.unaccounted = 0;
    }

    return totals;
  }, [byDate, totalHours, expected, start, end, hours]);
}

export function useCalculateTotalWorkingHours(start, end, hours) {
  return useMemo(function calculateWorkingHours() {
    if (start.isSame(end, "day") || end.isBefore(start)) {
      const currentWorkday = getCurrentWorkday();

      if (start.isSame(currentWorkday, "day")) {
        return 0;
      }

      return getExpectedHoursFor(start.toDate(), hours);
    }

    try {
      return eachDayOfInterval({ start: start.toDate(), end: end.toDate() }).reduce(function(sum, date) {
        return sum + getExpectedHoursFor(date, hours);
      }, 0);
    } catch (_) {
      return 0;
    }
  }, [start, end, hours]);
}

function getExpectedHoursFor(date, hours) {
  const weekday = getISODay(date);

  const index = weekday - 1;

  return get(hours, index, 0);
}



// WEBPACK FOOTER //
// ./app/components/History/TodoWarning.jsx